/**
 * Die Regeln des Koordinierungsmoduls — rein, ohne DOM, ohne Supabase.
 *
 * Das Projekt uebersetzt seine Tests in der Node-Umgebung; es gibt kein jsdom.
 * Eine Regel ist deshalb nur dann geprueft, wenn sie HIER steht und nicht in
 * einer Komponente. Stehende Entscheidung im Repo: duenner Container, Logik in
 * Hooks und puren Funktionen (`PlanungModul.tsx`, ProjectHub).
 *
 * Aufgabenteilung im Ordner:
 *   `gewerkeKatalog.ts`   — WELCHE Gewerke es gibt und was zwischen ihnen
 *                           passiert (Normalisierung + Regelkatalog).
 *   `ueberschneidung.ts`  — WANN sich zwei Zeitraeume beruehren (reine
 *                           Datumsarithmetik, ohne `Date`).
 *   diese Datei           — was daraus fuer ein PROJEKT folgt: Gewerke aus den
 *                           Arbeitsschritten bilden, gerechnete gegen
 *                           gespeicherte Paare abgleichen, Vorschlaege
 *                           erzeugen, Matrix und Zeitachse aufbereiten.
 *
 * ═══ DER LEITSATZ ══════════════════════════════════════════════════════════
 * Die Ueberschneidung ist jederzeit neu rechenbar — die BEWERTUNG ist es
 * nicht. Deshalb wird nie eine gespeicherte Zeile geloescht, weil sich der
 * Terminplan verschoben hat; sie wird als veraltet MARKIERT. Wer die Bewertung
 * wegwirft, wirft den beweiskraeftigen Teil weg und behaelt den, den ein
 * Rechner in Millisekunden wiederherstellt.
 */

import type { Arbeitsschritt } from '../sige-plan/types';
import {
    ALLGEMEINE_REGELN,
    GEWERK_REGELN,
    KATALOG_VERSION,
    RAUM_LABEL,
    SCHWELLE_KOORDINIERUNGSBESPRECHUNG,
    UNBEKANNT,
    findePaarung,
    gewerkAusText,
    gewerkBezeichnung,
    normalisiereText,
    type AllgemeineRegel,
    type GewerkId,
    type GewerkTreffer,
    type RaumBezug,
    type Schwere,
} from './gewerkeKatalog';
import {
    alsTag,
    ampel,
    berechneAlleUeberschneidungen,
    gewerkeOhneZeitraum,
    tageEinschliesslich,
    zeitraumMangel,
} from './ueberschneidung';
import type { Ampel, GewerkOhneZeitraum, GewerkZeitraum, UeberschneidungsPaar } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// KLEINKRAM
// ═══════════════════════════════════════════════════════════════════════════

/** Lexikalischer Vergleich ohne `localeCompare`: dessen Ergebnis haengt an der
 *  ICU-Zusammenstellung der Laufzeit, und eine Reihenfolge, die sich zwischen
 *  Browser und Node unterscheidet, ist keine Reihenfolge. Dieselbe Ueberlegung
 *  steht in `ueberschneidung.ts`. */
const vgl = (x: string, y: string): number => (x < y ? -1 : x > y ? 1 : 0);

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export type Prioritaet = 'hoch' | 'mittel' | 'niedrig';

/**
 * Schwere des Katalogs -> Prioritaet der Datenbank.
 *
 * Der Katalog sagt `gering`, die Spalte `prioritaet` kennt nur
 * `hoch|mittel|niedrig` (CHECK in `supabase/handbuch_koordinierung.sql`). Die
 * Abbildung steht genau einmal hier, damit nicht jede Aufrufstelle neu
 * entscheidet — und damit `SCHWERE_ZU_SEVERITY` (Richtung Gefaehrdungsmatrix)
 * daneben stehenbleiben kann, ohne dass jemand die beiden verwechselt.
 */
export const SCHWERE_ZU_PRIORITAET: Record<Schwere, Prioritaet> = {
    hoch: 'hoch',
    mittel: 'mittel',
    gering: 'niedrig',
};

const PRIO_RANG: Record<Prioritaet, number> = { hoch: 0, mittel: 1, niedrig: 2 };

/** Ordnet zwei Prioritaeten: die schwerere gewinnt. `null` ist die leichteste. */
export const schwerere = (a: Prioritaet | null, b: Prioritaet | null): Prioritaet | null => {
    if (!a) return b;
    if (!b) return a;
    return PRIO_RANG[a] <= PRIO_RANG[b] ? a : b;
};

// ═══════════════════════════════════════════════════════════════════════════
// 1) GEWERKE AUS DEN ARBEITSSCHRITTEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vorsilbe des Schluessels fuer ein Gewerk, das der Katalog nicht kennt.
 *
 * ─── Warum nicht alle Unbekannten in EINEN Topf ───────────────────────────
 * `gewerkAusText` liefert fuer jeden nicht zuordenbaren Freitext dasselbe
 * `unbekannt`. Wuerde daraus EIN Gewerk, fielen "Sonderfirma Nord" und
 * "Tiefkuehlmontage GmbH" zusammen — zwei fremde Firmen in einer Zeile, die
 * sich dann auch noch nicht mehr gegenseitig paaren koennten. Jeder unbekannte
 * Freitext behaelt deshalb seinen eigenen, aus dem Wortlaut abgeleiteten
 * Schluessel.
 */
export const UNBEKANNT_PRAEFIX = 'unbekannt:';

/** Schluessel fuer Arbeitsschritte ganz ohne Gewerksangabe. Sie verschwinden
 *  nicht — ein leeres Feld ist eine Fehlstelle im Plan und gehoert sichtbar in
 *  die Liste. */
export const OHNE_ANGABE = `${UNBEKANNT_PRAEFIX}ohne-angabe`;

export const OHNE_ANGABE_LABEL = '(ohne Gewerksangabe)';

/**
 * Der maschinelle Schluessel eines Gewerks aus seinem Freitext.
 *
 * Kennt der Katalog den Text, ist der Schluessel die Gewerk-Id (`elektro`).
 * Sonst die normalisierte Schreibweise mit Vorsilbe (`unbekannt:sonderbau`).
 *
 * Der Schluessel ist STABIL: er haengt allein am Wortlaut im Plan und NICHT an
 * einer Zuordnung, die jemand von Hand vornimmt. Genau daran haengt die
 * Wiedererkennung gespeicherter Bewertungen — waere die Zuordnung Teil des
 * Schluessels, saehe der naechste Leser (ohne diese Zuordnung) seine eigenen
 * bestaetigten Zeilen als "veraltet" und daneben dieselbe Paarung noch einmal
 * als "unbewertet".
 */
export const gewerkSchluessel = (rohGewerk: string | null | undefined): string => {
    const treffer = gewerkAusText(rohGewerk);
    if (treffer !== UNBEKANNT) return treffer;
    const slug = normalisiereText(text(rohGewerk));
    return slug ? `${UNBEKANNT_PRAEFIX}${slug}` : OHNE_ANGABE;
};

/** Ist dieser Schluessel ein Katalog-Gewerk? */
export const istKatalogGewerk = (norm: string): boolean =>
    typeof norm === 'string' && norm.length > 0 && !norm.startsWith(UNBEKANNT_PRAEFIX);

/**
 * Ein Gewerk des Projekts: alle Arbeitsschritte mit demselben Schluessel
 * zusammengefasst.
 */
export interface GewerkAusPlan extends GewerkZeitraum {
    /** Katalogtreffer — nach einer etwaigen Zuordnung von Hand. */
    treffer: GewerkTreffer;
    /** `false` heisst: der Regelkatalog hat zu diesem Gewerk nichts zu sagen. */
    zugeordnet: boolean;
    /** `true`, wenn `treffer` NUR durch eine Zuordnung von Hand zustande kam. */
    vonHandZugeordnet: boolean;
    /** Alle Schreibweisen aus dem Plan, haeufigste zuerst. */
    schreibweisen: string[];
    schrittIds: string[];
    anzahlSchritte: number;
    /** Wieviele der Schritte selbst keinen verwertbaren Zeitraum haben. */
    schritteOhneDatum: number;
    /** Distinkte `raeumliche_zuordnung` — FREITEXT. Wird angezeigt, nie
     *  ausgewertet (siehe `raumfrage`). */
    bereiche: string[];
}

interface Sammler {
    norm: string;
    schreibweisen: Map<string, { anzahl: number; zuerst: number }>;
    von: string | null;
    bis: string | null;
    schrittIds: string[];
    schritteOhneDatum: number;
    bereiche: string[];
}

/**
 * Bildet aus den Arbeitsschritten die Gewerke des Projekts.
 *
 * Zeitraum = Huelle: fruehester verwertbarer Beginn, spaetestes verwertbares
 * Ende ueber alle Schritte des Gewerks. Ein einzelner Schritt ohne Datum
 * verkleinert die Huelle nicht — er wird nur gezaehlt (`schritteOhneDatum`),
 * damit die Oberflaeche sagen kann, wie belastbar der Zeitraum ist.
 *
 * ═══ `schritteOhneDatum` IST KEINE NEBENZAHL ═════════════════════════════
 * Sie ist die einzige Spur, die ein halb datierter Arbeitsschritt hinterlaesst.
 * Ein Schritt mit Beginn, aber ohne Ende zieht die Huelle NICHT auf, und das
 * Gewerk bleibt trotzdem rechenbar — es faellt also auch nicht in die
 * Ausschussliste. Damit verschwindet der Schritt spurlos: die Matrix zeigt
 * grau „keine gemeinsame Zeit", obwohl der Plan das Gewerk zu dieser Zeit
 * fuehrt. Wer diese Zahl nicht anzeigt, zeigt eine echte Ueberschneidung als
 * geprueft und unbedenklich an. Sie gehoert deshalb an JEDES Gewerk in
 * Matrix, Tabelle und PDF (`gewerkeMitLuecke`).
 *
 * `zuordnungen` bildet einen unbekannten Schluessel auf ein Katalog-Gewerk ab.
 * Sie aendert AUSSCHLIESSLICH `treffer`/`zugeordnet` — nie `norm` (siehe
 * `gewerkSchluessel`).
 *
 * Der Anzeigename ist die haeufigste Schreibweise; bei Gleichstand die zuerst
 * im Plan verwendete. Nicht die alphabetisch erste: der Anwender soll seinen
 * eigenen Wortlaut wiederfinden, und "zuerst benutzt" ist die Schreibweise,
 * mit der er das Gewerk angelegt hat.
 */
export const gewerkeAusSchritten = (
    schritte: Arbeitsschritt[],
    zuordnungen: Record<string, GewerkId> = {},
): GewerkAusPlan[] => {
    const liste = Array.isArray(schritte) ? schritte : [];
    const nachNorm = new Map<string, Sammler>();

    liste.forEach((schritt, index) => {
        if (!schritt) return;
        const roh = text(schritt.gewerk);
        const norm = gewerkSchluessel(roh);
        const anzeige = roh || OHNE_ANGABE_LABEL;

        let s = nachNorm.get(norm);
        if (!s) {
            s = {
                norm,
                schreibweisen: new Map(),
                von: null,
                bis: null,
                schrittIds: [],
                schritteOhneDatum: 0,
                bereiche: [],
            };
            nachNorm.set(norm, s);
        }

        const vorhanden = s.schreibweisen.get(anzeige);
        if (vorhanden) vorhanden.anzahl += 1;
        else s.schreibweisen.set(anzeige, { anzahl: 1, zuerst: index });

        if (schritt.id) s.schrittIds.push(String(schritt.id));

        const von = alsTag(schritt.zeitraum_von);
        const bis = alsTag(schritt.zeitraum_bis);
        if (von && (!s.von || von < s.von)) s.von = von;
        if (bis && (!s.bis || bis > s.bis)) s.bis = bis;
        if (zeitraumMangel({ von: schritt.zeitraum_von || null, bis: schritt.zeitraum_bis || null })) {
            s.schritteOhneDatum += 1;
        }

        const bereich = text(schritt.raeumliche_zuordnung);
        if (bereich && s.bereiche.indexOf(bereich) < 0) s.bereiche.push(bereich);
    });

    const gewerke: GewerkAusPlan[] = [];
    nachNorm.forEach(s => {
        const schreibweisen = [...s.schreibweisen.entries()]
            .sort((a, b) => b[1].anzahl - a[1].anzahl || a[1].zuerst - b[1].zuerst)
            .map(e => e[0]);

        const katalog = istKatalogGewerk(s.norm);
        const handZuordnung = katalog ? null : ((zuordnungen && zuordnungen[s.norm]) || null);
        const treffer: GewerkTreffer = katalog ? (s.norm as GewerkId) : (handZuordnung || UNBEKANNT);

        gewerke.push({
            norm: s.norm,
            label: schreibweisen[0] || OHNE_ANGABE_LABEL,
            von: s.von,
            bis: s.bis,
            treffer,
            zugeordnet: treffer !== UNBEKANNT,
            vonHandZugeordnet: !katalog && !!handZuordnung,
            schreibweisen,
            schrittIds: s.schrittIds,
            anzahlSchritte: s.schrittIds.length,
            schritteOhneDatum: s.schritteOhneDatum,
            bereiche: s.bereiche,
        });
    });

    // Kanonische Reihenfolge: nach Schluessel. Ansichten sortieren fuer sich um
    // (`sortiereNachZeit`), aber das Ergebnis dieser Funktion darf nicht von der
    // Reihenfolge der Datenbankantwort abhaengen.
    gewerke.sort((a, b) => vgl(a.norm, b.norm));
    return gewerke;
};

/** Die Gewerke, mit denen ueberhaupt gerechnet werden kann. */
export const rechenbareGewerke = (gewerke: GewerkAusPlan[]): GewerkAusPlan[] =>
    (gewerke || []).filter(g => g && zeitraumMangel(g) === null);

/** Fuer Zeitachse und Matrix: nach Beginn, dann Ende, dann Schluessel. */
export const sortiereNachZeit = (gewerke: GewerkAusPlan[]): GewerkAusPlan[] =>
    [...(gewerke || [])].sort((a, b) =>
        vgl(a.von || '9999-99-99', b.von || '9999-99-99')
        || vgl(a.bis || '9999-99-99', b.bis || '9999-99-99')
        || vgl(a.norm, b.norm));

/** Die Gewerke, die der Katalog nicht kennt — sie verschwinden nie, sondern
 *  bekommen ein Zuordnungsangebot. */
export const nichtZugeordnete = (gewerke: GewerkAusPlan[]): GewerkAusPlan[] =>
    (gewerke || []).filter(g => g && !g.zugeordnet);

/**
 * Ein ausgeschlossenes Gewerk mit allem, was der Plan darueber weiss.
 *
 * `GewerkOhneZeitraum` aus `types.ts` fuehrt sein `gewerk` als `GewerkZeitraum`
 * — die Zeitrechnung braucht nicht mehr. Ansicht und PDF wollen aber sagen
 * koennen, wieviele Arbeitsschritte betroffen sind und wieviele davon gar kein
 * Datum haben; ohne diese Zahl liest sich die Liste wie eine Formalie.
 * `gewerkeOhneZeitraum` reicht die uebergebenen Objekte unveraendert durch,
 * die Einengung ist deshalb keine Behauptung, sondern eine Feststellung.
 */
export interface AusgeschlossenesGewerk extends GewerkOhneZeitraum {
    gewerk: GewerkAusPlan;
}

/** Durchgereicht, damit die Ansichten nur EINE Logikdatei kennen muessen. */
export const ausschussListe = (gewerke: GewerkAusPlan[]): AusgeschlossenesGewerk[] =>
    gewerkeOhneZeitraum(gewerke || []) as AusgeschlossenesGewerk[];

/**
 * Die Gewerke, die gerechnet WERDEN, deren Zeitraum aber unvollstaendig ist.
 *
 * Das ist die gefaehrliche Zwischenklasse: die Huelle ist gueltig, das Gewerk
 * steht in der Matrix und in jeder Paarung — und trotzdem ist mindestens ein
 * Arbeitsschritt gar nicht mitgerechnet worden. Die Ausschussliste kennt diese
 * Gewerke nicht (ihre Huelle ist ja in Ordnung), also braucht es diese zweite
 * Liste. Ohne sie sieht ein Plan mit einem Schritt „ab 20.09., Ende offen"
 * genauso aus wie ein vollstaendig geprueftes Projekt.
 *
 * Die Reihenfolge ist die kanonische aus `gewerkeAusSchritten` (nach
 * Schluessel) — die Liste landet im PDF und darf nicht wandern.
 */
export const gewerkeMitLuecke = (gewerke: GewerkAusPlan[]): GewerkAusPlan[] =>
    rechenbareGewerke(gewerke || []).filter(g => g.schritteOhneDatum > 0);

/** Arbeitsschritte ohne verwertbaren Zeitraum, ueber ALLE Gewerke gezaehlt —
 *  auch ueber die, die ohnehin schon aus der Pruefung fallen. */
export const schritteOhneDatumGesamt = (gewerke: GewerkAusPlan[]): number =>
    (gewerke || []).reduce((summe, g) => summe + (g ? g.schritteOhneDatum : 0), 0);

// ═══════════════════════════════════════════════════════════════════════════
// 2) DIE GESPEICHERTE ZEILE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Eine Zeile aus `public.koordinierung_paare`.
 *
 * Feldnamen wortgleich aus `supabase/handbuch_koordinierung.sql` (Migration
 * Nr. 40) — die Zeile geht unveraendert nach Supabase und kommt unveraendert
 * zurueck. Der Typ steht hier und nicht in `types.ts`, weil `types.ts` die
 * reine Zeitrechnung beschreibt und bewusst nichts von einer Datenbank weiss.
 */
export interface KoordinierungPaar {
    id: string;
    project_id: string;
    /** Vermerk auf die bewertete Planfassung. KEIN Fremdschluessel. */
    sige_projekt_id: string | null;
    gewerk_a_norm: string;
    gewerk_b_norm: string;
    gewerk_a_label: string;
    gewerk_b_label: string;
    ueberschneidung_von: string | null;
    ueberschneidung_bis: string | null;
    dauer_tage: number | null;
    gefaehrdung: string;
    massnahme: string;
    top: string | null;
    prioritaet: Prioritaet;
    quelle: 'regel' | 'ki' | 'hand';
    katalog_version: string | null;
    bestaetigt_am: string | null;
    bestaetigt_von_name: string | null;
    uebernommen_matrix_am: string | null;
    veraltet_seit: string | null;
    created_at?: string;
    updated_at?: string;
}

/** Was der Client beim Anlegen schickt. `project_id` setzt der Service. */
export type NeuesKoordinierungPaar = Omit<
    KoordinierungPaar,
    'id' | 'project_id' | 'created_at' | 'updated_at'
>;

/**
 * Bringt das Paar in die Reihenfolge, die die Datenbank verlangt
 * (`check (gewerk_a_norm < gewerk_b_norm)`).
 *
 * Ohne diese Klemme entstuenden je Paar zwei spiegelbildliche Zeilen mit
 * getrennten Bewertungen — und der Unique-Index griffe nicht.
 */
export const ordnePaar = <T extends { norm: string; label: string }>(a: T, b: T): {
    aNorm: string; aLabel: string; bNorm: string; bLabel: string;
} => {
    const erst = a.norm < b.norm ? a : b;
    const zweit = a.norm < b.norm ? b : a;
    return { aNorm: erst.norm, aLabel: erst.label, bNorm: zweit.norm, bLabel: zweit.label };
};

/** Schluessel eines Paares in Ansicht und Abgleich. */
export const paarKey = (aNorm: string, bNorm: string): string =>
    aNorm < bNorm ? `${aNorm}~${bNorm}` : `${bNorm}~${aNorm}`;

// ═══════════════════════════════════════════════════════════════════════════
// 3) VORSCHLAEGE AUS DEM KATALOG
// ═══════════════════════════════════════════════════════════════════════════

export interface Vorschlag {
    /** Katalogcode (GK-001, GR-01, AR-03). Unveraenderlich — daran erkennt man
     *  einen Vorschlag wieder, auch wenn der Text fortgeschrieben wird. */
    code: string;
    herkunft: 'paarung' | 'gewerk' | 'allgemein';
    /** Bei `herkunft: 'gewerk'`: von welchem der beiden die Regel ausgeht. */
    ausgehendVon: string | null;
    schwere: Schwere;
    prioritaet: Prioritaet;
    raumbezug: RaumBezug | null;
    /**
     * Rueckfrage an den Menschen, wenn die Paarung nur unter einer raeumlichen
     * Voraussetzung gilt.
     *
     * ─── Warum das NICHT automatisch entschieden wird ─────────────────────
     * `Arbeitsschritt.raeumliche_zuordnung` ist Freitext ("EG/1. OG",
     * "Achse C-F", "gesamte Baustelle", "siehe Plan"). Zwei solche Texte
     * maschinell auf "uebereinander" oder "nicht uebereinander" abzubilden
     * hiesse raten — und ein geratenes "nein" loescht eine echte Gefaehrdung
     * aus dem SiGe-Plan, ohne dass es jemand merkt. Der Text der beiden
     * Gewerke steht deshalb IN der Frage; entschieden wird sie von Hand.
     */
    raumfrage: string | null;
    gefaehrdung: string;
    massnahme: string;
    vorschriften: string[];
    /** Es gibt bereits eine gespeicherte Zeile mit genau diesem
     *  Gefaehrdungstext. Der Vorschlag wird trotzdem angezeigt — ausgeblendet
     *  koennte niemand mehr sehen, dass der Katalog ihn kennt. */
    bereitsGespeichert: boolean;
}

const raumfrageText = (raumbezug: RaumBezug, a: GewerkAusPlan, b: GewerkAusPlan): string | null => {
    if (raumbezug === 'baustellenweit') return null;
    const teil = (g: GewerkAusPlan) =>
        g.bereiche.length > 0
            ? `${g.label}: „${g.bereiche.join('“, „')}“`
            : `${g.label}: keine räumliche Zuordnung im Plan`;
    return `Diese Paarung gilt ${RAUM_LABEL[raumbezug]}. Im Plan steht — ${teil(a)}; ${teil(b)}. `
        + 'Bitte prüfen und entscheiden; automatisch beantwortet wird diese Frage nicht.';
};

/**
 * Was der Katalog zu diesem Gewerkepaar sagt.
 *
 * Drei Quellen, in dieser Reihenfolge:
 *   1. die Paarung selbst (`PAARUNGEN`) — nur wenn BEIDE Gewerke zugeordnet sind,
 *   2. die Regeln, die ein Gewerk gegen jedes andere erzeugt (`GEWERK_REGELN`),
 *   3. die allgemeine Regel AR-03, sobald eines der beiden nicht zugeordnet ist.
 *
 * AR-03 ist kein Beiwerk: ein nicht zugeordnetes Gewerk ist KEIN Nachweis der
 * Gefaehrdungsfreiheit. Ohne diesen Eintrag saehe ein Paar aus zwei
 * Freitext-Gewerken aus wie ein geprueftes Paar ohne Befund.
 *
 * Die Reihenfolge ist fest (Prioritaet, dann Code) — sie landet im PDF.
 */
export const vorschlaegeFuerPaar = (
    a: GewerkAusPlan,
    b: GewerkAusPlan,
    gespeichert: KoordinierungPaar[] = [],
): Vorschlag[] => {
    if (!a || !b) return [];
    const vorhandeneTexte = new Set((gespeichert || []).map(z => text(z && z.gefaehrdung)));
    const roh: Vorschlag[] = [];

    if (a.zugeordnet && b.zugeordnet && a.treffer !== b.treffer) {
        const paarung = findePaarung(a.treffer as GewerkId, b.treffer as GewerkId);
        if (paarung) {
            roh.push({
                code: paarung.code,
                herkunft: 'paarung',
                ausgehendVon: null,
                schwere: paarung.schwere,
                prioritaet: SCHWERE_ZU_PRIORITAET[paarung.schwere],
                raumbezug: paarung.raumbezug,
                raumfrage: raumfrageText(paarung.raumbezug, a, b),
                gefaehrdung: paarung.gefaehrdung,
                massnahme: paarung.massnahme,
                vorschriften: paarung.vorschriften || [],
                bereitsGespeichert: false,
            });
        }
    }

    // Regeln "ein Gewerk gegen alle": beide Seiten pruefen. Sind BEIDE Gewerke
    // Traeger derselben Regel (zwei Kranfirmen), steht sie trotzdem nur einmal
    // da — der Gefahrbereich ist derselbe.
    const gesehen = new Set<string>();
    [a, b].forEach(g => {
        if (!g.zugeordnet) return;
        GEWERK_REGELN.forEach(regel => {
            if (regel.gewerk !== g.treffer || gesehen.has(regel.code)) return;
            gesehen.add(regel.code);
            roh.push({
                code: regel.code,
                herkunft: 'gewerk',
                ausgehendVon: g.label,
                schwere: regel.schwere,
                prioritaet: SCHWERE_ZU_PRIORITAET[regel.schwere],
                raumbezug: regel.raumbezug,
                raumfrage: raumfrageText(regel.raumbezug, a, b),
                gefaehrdung: regel.gefaehrdung,
                massnahme: regel.massnahme,
                vorschriften: regel.vorschriften || [],
                bereitsGespeichert: false,
            });
        });
    });

    if (!a.zugeordnet || !b.zugeordnet) {
        const ar03 = ALLGEMEINE_REGELN.find(r => r.bedingung === 'gewerk_unbekannt');
        if (ar03) {
            roh.push({
                code: ar03.code,
                herkunft: 'allgemein',
                ausgehendVon: !a.zugeordnet ? a.label : b.label,
                schwere: ar03.schwere,
                prioritaet: SCHWERE_ZU_PRIORITAET[ar03.schwere],
                raumbezug: null,
                raumfrage: null,
                gefaehrdung: ar03.hinweis,
                massnahme: ar03.massnahme,
                vorschriften: ar03.vorschriften || [],
                bereitsGespeichert: false,
            });
        }
    }

    roh.forEach(v => { v.bereitsGespeichert = vorhandeneTexte.has(text(v.gefaehrdung)); });
    roh.sort((x, y) => PRIO_RANG[x.prioritaet] - PRIO_RANG[y.prioritaet] || vgl(x.code, y.code));
    return roh;
};

/**
 * Baut aus einem Vorschlag die Zeile, die gespeichert wird.
 *
 * `bestaetigt_am` bleibt LEER. Ein Vorschlag ist genau das: ein Vorschlag, bis
 * ihn ein Mensch bestaetigt. Wer hier gleich stempelte, machte aus einem
 * Katalogtreffer eine Feststellung des Koordinators.
 *
 * `katalog_version` wandert mit an die Zeile: waechst der Katalog spaeter,
 * aendert sich nicht rueckwirkend, was in diesem Projekt galt.
 */
export const paarAusVorschlag = (
    zeile: { aNorm: string; aLabel: string; bNorm: string; bLabel: string; berechnet: UeberschneidungsPaar | null },
    vorschlag: Vorschlag,
    sigeProjektId: string | null = null,
): NeuesKoordinierungPaar => ({
    sige_projekt_id: sigeProjektId || null,
    gewerk_a_norm: zeile.aNorm,
    gewerk_b_norm: zeile.bNorm,
    gewerk_a_label: zeile.aLabel || '',
    gewerk_b_label: zeile.bLabel || '',
    ueberschneidung_von: zeile.berechnet ? zeile.berechnet.von : null,
    ueberschneidung_bis: zeile.berechnet ? zeile.berechnet.bis : null,
    dauer_tage: zeile.berechnet ? zeile.berechnet.dauerTage : null,
    gefaehrdung: vorschlag.gefaehrdung,
    massnahme: vorschlag.massnahme || '',
    top: null,
    prioritaet: vorschlag.prioritaet,
    quelle: 'regel',
    katalog_version: String(KATALOG_VERSION),
    bestaetigt_am: null,
    bestaetigt_von_name: null,
    uebernommen_matrix_am: null,
    veraltet_seit: null,
});

/** Eine leere Zeile fuer einen Eintrag von Hand (`quelle: 'hand'`). */
export const leeresPaar = (
    zeile: { aNorm: string; aLabel: string; bNorm: string; bLabel: string; berechnet: UeberschneidungsPaar | null },
    gefaehrdung: string,
): NeuesKoordinierungPaar => ({
    sige_projekt_id: null,
    gewerk_a_norm: zeile.aNorm,
    gewerk_b_norm: zeile.bNorm,
    gewerk_a_label: zeile.aLabel || '',
    gewerk_b_label: zeile.bLabel || '',
    ueberschneidung_von: zeile.berechnet ? zeile.berechnet.von : null,
    ueberschneidung_bis: zeile.berechnet ? zeile.berechnet.bis : null,
    dauer_tage: zeile.berechnet ? zeile.berechnet.dauerTage : null,
    gefaehrdung: text(gefaehrdung),
    massnahme: '',
    top: null,
    prioritaet: 'mittel',
    quelle: 'hand',
    katalog_version: String(KATALOG_VERSION),
    bestaetigt_am: null,
    bestaetigt_von_name: null,
    uebernommen_matrix_am: null,
    veraltet_seit: null,
});

// ═══════════════════════════════════════════════════════════════════════════
// 4) ABGLEICH — berechnet gegen gespeichert
// ═══════════════════════════════════════════════════════════════════════════

export interface Fenster {
    von: string | null;
    bis: string | null;
    dauerTage: number | null;
}

/**
 * Wie eine gespeicherte Zeile zur aktuellen Rechnung steht.
 *
 * `veraltet`       — es gibt keine Ueberschneidung mehr. NICHT loeschen.
 * `geaendert`      — es gibt sie noch, aber mit anderem Zeitfenster. Auch dann,
 *                    wenn die Zeile als veraltet gekennzeichnet war: ein
 *                    verschobenes Fenster bleibt ein verschobenes Fenster.
 * `wieder_aktuell` — sie war als veraltet markiert und ueberschneidet sich
 *                    wieder MIT DEMSELBEN Fenster.
 * `aktuell`        — Rechnung und Zeile stimmen ueberein.
 */
export type ZeilenBefund = 'aktuell' | 'geaendert' | 'veraltet' | 'wieder_aktuell';

export interface GespeicherteBewertung {
    zeile: KoordinierungPaar;
    befund: ZeilenBefund;
    /** Was in der Zeile steht. */
    alt: Fenster;
    /** Was jetzt gerechnet wird — `null`, wenn es keine Ueberschneidung gibt. */
    neu: Fenster | null;
}

/** Gesamtlage eines Gewerkepaares. */
export type PaarStatus = 'unbewertet' | 'bewertet' | 'geaendert' | 'veraltet';

export interface AbgleichZeile {
    schluessel: string;
    aNorm: string;
    aLabel: string;
    bNorm: string;
    bLabel: string;
    status: PaarStatus;
    /** Die aktuelle Rechnung. `null` bei einer veralteten Bewertung. */
    berechnet: UeberschneidungsPaar | null;
    bewertungen: GespeicherteBewertung[];
    vorschlaege: Vorschlag[];
    /** Wieviele Bewertungen ein Mensch bestaetigt hat. */
    bestaetigt: number;
    /** Wieviele noch auf die Bestaetigung warten. */
    unbestaetigt: number;
    hoechstePrioritaet: Prioritaet | null;
    dauerTage: number;
    ampel: Ampel;
}

export interface AbgleichKennzahlen {
    gewerke: number;
    gewerkeOhneZeitraum: number;
    /** Gewerke, die gerechnet werden, aber undatierte Arbeitsschritte tragen.
     *  Getrennt von `gewerkeOhneZeitraum`: die einen fehlen in der Matrix, die
     *  anderen stehen darin und sehen dabei vollstaendig aus. */
    gewerkeMitLuecke: number;
    /** Arbeitsschritte ohne verwertbaren Zeitraum, ueber alle Gewerke. */
    schritteOhneDatum: number;
    nichtZugeordnet: number;
    paare: number;
    unbewertet: number;
    bewertet: number;
    geaendert: number;
    veraltet: number;
    bestaetigt: number;
    unbestaetigt: number;
}

export interface AbgleichErgebnis {
    zeilen: AbgleichZeile[];
    kennzahlen: AbgleichKennzahlen;
    /**
     * Zeilen-Ids, deren `veraltet_seit` gesetzt werden muss.
     *
     * Ausdruecklich eine Liste zum MARKIEREN und nicht zum Loeschen: die
     * Bewertung ist der beweiskraeftige Teil, das Datum ist jederzeit neu
     * rechenbar. Wer sie loescht, verliert den Nachweis, dass eine Gefaehrdung
     * einmal erkannt und geregelt war.
     */
    zuVeralten: string[];
    /** Zeilen-Ids, deren `veraltet_seit` aufgehoben werden kann. */
    zuReaktivieren: string[];
}

const fensterAus = (z: KoordinierungPaar): Fenster => ({
    von: alsTag(z.ueberschneidung_von),
    bis: alsTag(z.ueberschneidung_bis),
    dauerTage: typeof z.dauer_tage === 'number' ? z.dauer_tage : null,
});

const fensterLeer = (f: Fenster): boolean => !f.von && !f.bis && f.dauerTage == null;

const fensterGleich = (f: Fenster, p: UeberschneidungsPaar): boolean =>
    f.von === p.von && f.bis === p.bis && (f.dauerTage == null || f.dauerTage === p.dauerTage);

/**
 * Der Abgleich zwischen dem, was der Plan HEUTE hergibt, und dem, was bereits
 * bewertet wurde. Kernstueck des Moduls.
 *
 * Drei Faelle und ihre Behandlung:
 *
 *   berechnetes Paar OHNE Zeile         -> `unbewertet`
 *       Es fehlt eine Bewertung. Nichts wird automatisch angelegt: eine
 *       ungefragt erzeugte Bewertung waere eine Feststellung, die niemand
 *       getroffen hat.
 *
 *   Zeile OHNE aktuelle Ueberschneidung -> `veraltet`
 *       Die Zeile bleibt stehen und bekommt `veraltet_seit`. Loeschen kaeme
 *       nicht in Frage: nach einer Terminverschiebung stuende sonst in der
 *       Akte, die Gefaehrdung habe es nie gegeben.
 *
 *   Zeile mit VERSCHOBENEM Fenster      -> `geaendert`
 *       Alt und Neu werden beide gefuehrt; ueberschrieben wird nichts
 *       automatisch. Was der Koordinator bewertet hat, bezog sich auf den
 *       alten Zeitraum — ob es fuer den neuen noch gilt, entscheidet er.
 *
 * Eine Zeile mit leerem Fenster (von Hand angelegt, ohne Terminbezug) gilt nie
 * als `geaendert`: es gibt nichts zu vergleichen.
 */
export const abgleich = (
    berechnet: UeberschneidungsPaar[],
    gespeichert: KoordinierungPaar[],
    gewerke: GewerkAusPlan[] = [],
): AbgleichErgebnis => {
    const paare = Array.isArray(berechnet) ? berechnet.filter(Boolean) : [];
    const zeilenRoh = Array.isArray(gespeichert) ? gespeichert.filter(Boolean) : [];
    const gewerkeListe = Array.isArray(gewerke) ? gewerke.filter(Boolean) : [];

    const gewerkNachNorm = new Map<string, GewerkAusPlan>();
    gewerkeListe.forEach(g => gewerkNachNorm.set(g.norm, g));

    const berechnetNachKey = new Map<string, UeberschneidungsPaar>();
    paare.forEach(p => berechnetNachKey.set(paarKey(p.aNorm, p.bNorm), p));

    const zeilenNachKey = new Map<string, KoordinierungPaar[]>();
    zeilenRoh.forEach(z => {
        const key = paarKey(text(z.gewerk_a_norm), text(z.gewerk_b_norm));
        const liste = zeilenNachKey.get(key);
        if (liste) liste.push(z); else zeilenNachKey.set(key, [z]);
    });

    const zuVeralten: string[] = [];
    const zuReaktivieren: string[] = [];
    const ergebnis: AbgleichZeile[] = [];

    /** Ein Ersatz-Gewerk fuer Schluessel, die nur noch in der Datenbank stehen
     *  — der Plan kennt sie nicht mehr. Ohne diesen Ersatz haette die veraltete
     *  Zeile keinen Anzeigenamen. */
    const ersatzGewerk = (norm: string, label: string): GewerkAusPlan => ({
        norm,
        label: label || (istKatalogGewerk(norm) ? gewerkBezeichnung(norm as GewerkId) : norm),
        von: null,
        bis: null,
        treffer: istKatalogGewerk(norm) ? (norm as GewerkId) : UNBEKANNT,
        zugeordnet: istKatalogGewerk(norm),
        vonHandZugeordnet: false,
        schreibweisen: label ? [label] : [],
        schrittIds: [],
        anzahlSchritte: 0,
        schritteOhneDatum: 0,
        bereiche: [],
    });

    const baue = (
        key: string,
        aNorm: string, aLabel: string, bNorm: string, bLabel: string,
        rechnung: UeberschneidungsPaar | null,
    ): AbgleichZeile => {
        const zeilen = (zeilenNachKey.get(key) || []).slice().sort((x, y) =>
            vgl(text(x.gefaehrdung), text(y.gefaehrdung)) || vgl(String(x.id), String(y.id)));

        const bewertungen: GespeicherteBewertung[] = zeilen.map(z => {
            const alt = fensterAus(z);
            let befund: ZeilenBefund;
            // ═══ ERST DAS FENSTER, DANN DIE KENNZEICHNUNG ════════════════
            // `veraltet_seit` sagt nur, dass die Zeile einmal ohne
            // Ueberschneidung dastand — es sagt NICHTS darueber, ob das
            // wiedergekehrte Zeitfenster dasselbe ist. Stuende die
            // Kennzeichnung vor dem Vergleich, ginge eine bestaetigte Zeile
            // vom Juni mit einem Plan vom Oktober als „wieder aktuell" durch:
            // gruene Kachel, kein Uebernahme-Knopf, und der Bericht druckt die
            // Festlegung mit dem alten Zeitraum. Deshalb entscheidet zuerst der
            // Fenstervergleich; die Kennzeichnung waehlt danach nur noch
            // zwischen `aktuell` und `wieder_aktuell`.
            if (!rechnung) befund = 'veraltet';
            else if (fensterLeer(alt) || fensterGleich(alt, rechnung)) {
                befund = z.veraltet_seit ? 'wieder_aktuell' : 'aktuell';
            } else befund = 'geaendert';

            if (befund === 'veraltet' && !z.veraltet_seit) zuVeralten.push(String(z.id));
            if (befund === 'wieder_aktuell') zuReaktivieren.push(String(z.id));

            return {
                zeile: z,
                befund,
                alt,
                neu: rechnung ? { von: rechnung.von, bis: rechnung.bis, dauerTage: rechnung.dauerTage } : null,
            };
        });

        let status: PaarStatus;
        if (!rechnung) status = 'veraltet';
        else if (bewertungen.length === 0) status = 'unbewertet';
        else if (bewertungen.some(b => b.befund === 'geaendert')) status = 'geaendert';
        else status = 'bewertet';

        const a = gewerkNachNorm.get(aNorm) || ersatzGewerk(aNorm, aLabel);
        const b = gewerkNachNorm.get(bNorm) || ersatzGewerk(bNorm, bLabel);

        let hoechste: Prioritaet | null = null;
        bewertungen.forEach(bw => { hoechste = schwerere(hoechste, bw.zeile.prioritaet); });

        const vorschlaege = vorschlaegeFuerPaar(a, b, zeilen);
        if (!hoechste) vorschlaege.forEach(v => { hoechste = schwerere(hoechste, v.prioritaet); });

        return {
            schluessel: key,
            aNorm,
            bNorm,
            aLabel: a.label || aLabel,
            bLabel: b.label || bLabel,
            status,
            berechnet: rechnung,
            bewertungen,
            vorschlaege,
            bestaetigt: bewertungen.filter(bw => !!bw.zeile.bestaetigt_am).length,
            unbestaetigt: bewertungen.filter(bw => !bw.zeile.bestaetigt_am).length,
            hoechstePrioritaet: hoechste,
            dauerTage: rechnung ? rechnung.dauerTage : 0,
            ampel: rechnung ? rechnung.ampel : ampel(0),
        };
    };

    // Zuerst die gerechneten Paare — `berechneAlleUeberschneidungen` liefert sie
    // bereits in fester Reihenfolge (Dauer absteigend).
    paare.forEach(p => {
        ergebnis.push(baue(paarKey(p.aNorm, p.bNorm), p.aNorm, p.aLabel, p.bNorm, p.bLabel, p));
    });

    // Danach die Bewertungen ohne aktuelle Ueberschneidung.
    const veraltete: AbgleichZeile[] = [];
    zeilenNachKey.forEach((zeilen, key) => {
        if (berechnetNachKey.has(key)) return;
        const erste = zeilen[0];
        veraltete.push(baue(
            key,
            text(erste.gewerk_a_norm), text(erste.gewerk_a_label),
            text(erste.gewerk_b_norm), text(erste.gewerk_b_label),
            null,
        ));
    });
    veraltete.sort((x, y) => vgl(x.aNorm, y.aNorm) || vgl(x.bNorm, y.bNorm));
    ergebnis.push(...veraltete);

    const zaehle = (s: PaarStatus) => ergebnis.filter(z => z.status === s).length;
    const ausschuss = ausschussListe(gewerkeListe);

    return {
        zeilen: ergebnis,
        kennzahlen: {
            gewerke: gewerkeListe.length,
            gewerkeOhneZeitraum: ausschuss.length,
            gewerkeMitLuecke: gewerkeMitLuecke(gewerkeListe).length,
            schritteOhneDatum: schritteOhneDatumGesamt(gewerkeListe),
            nichtZugeordnet: nichtZugeordnete(gewerkeListe).length,
            paare: ergebnis.length,
            unbewertet: zaehle('unbewertet'),
            bewertet: zaehle('bewertet'),
            geaendert: zaehle('geaendert'),
            veraltet: zaehle('veraltet'),
            bestaetigt: ergebnis.reduce((s, z) => s + z.bestaetigt, 0),
            unbestaetigt: ergebnis.reduce((s, z) => s + z.unbestaetigt, 0),
        },
        zuVeralten,
        zuReaktivieren,
    };
};

/** Der ganze Weg in einem Aufruf: Gewerke bilden, rechnen, abgleichen. */
export const analysiere = (
    schritte: Arbeitsschritt[],
    gespeichert: KoordinierungPaar[],
    zuordnungen: Record<string, GewerkId> = {},
): { gewerke: GewerkAusPlan[]; paare: UeberschneidungsPaar[]; ergebnis: AbgleichErgebnis } => {
    const gewerke = gewerkeAusSchritten(schritte, zuordnungen);
    const paare = berechneAlleUeberschneidungen(rechenbareGewerke(gewerke));
    return { gewerke, paare, ergebnis: abgleich(paare, gespeichert, gewerke) };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5) LISTE — sortieren und filtern
// ═══════════════════════════════════════════════════════════════════════════

export interface ListenFilter {
    nurUnbewertet?: boolean;
    nurVeraltet?: boolean;
    nurUnbestaetigt?: boolean;
}

/** Schwere zuerst, dann die Dauer. Beide Kriterien zusammen — die laengste
 *  Ueberschneidung ist nicht automatisch die gefaehrlichste. */
export const sortiereZeilen = (zeilen: AbgleichZeile[]): AbgleichZeile[] =>
    [...(zeilen || [])].sort((x, y) =>
        (x.hoechstePrioritaet ? PRIO_RANG[x.hoechstePrioritaet] : 3)
        - (y.hoechstePrioritaet ? PRIO_RANG[y.hoechstePrioritaet] : 3)
        || y.dauerTage - x.dauerTage
        || vgl(x.aNorm, y.aNorm)
        || vgl(x.bNorm, y.bNorm));

export const filterZeilen = (zeilen: AbgleichZeile[], filter: ListenFilter = {}): AbgleichZeile[] =>
    (zeilen || []).filter(z => {
        if (filter.nurUnbewertet && z.status !== 'unbewertet') return false;
        if (filter.nurVeraltet && z.status !== 'veraltet') return false;
        if (filter.nurUnbestaetigt && z.unbestaetigt === 0) return false;
        return true;
    });

/**
 * Wie eine bestaetigte Festlegung zum HEUTIGEN Terminstand steht.
 *
 * `aktuell`   — der Plan gibt genau dieses Zeitfenster noch her.
 * `geaendert` — es gibt die Ueberschneidung noch, aber verschoben.
 * `veraltet`  — der Plan sieht die beiden Gewerke nicht mehr gleichzeitig vor.
 */
export type FestlegungsStand = 'aktuell' | 'geaendert' | 'veraltet';

export interface BestaetigteFestlegung {
    zeile: AbgleichZeile;
    bewertung: GespeicherteBewertung;
    stand: FestlegungsStand;
    /** Fertiger Satz fuer Bericht und Oberflaeche, damit nicht jede Ansicht
     *  ihre eigene Uebersetzung erfindet (wie `hinweis` beim Ausschuss). */
    standText: string;
}

/** ISO-Tagesdatum als deutsches Datum, rein zeichenweise — ohne `Date`, weil
 *  dessen Ergebnis an der Zeitzone der Laufzeit haengt. */
const deutschesDatum = (iso: string | null | undefined): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '';

const standAus = (b: GespeicherteBewertung): { stand: FestlegungsStand; standText: string } => {
    if (b.befund === 'veraltet') {
        const seit = deutschesDatum((b.zeile.veraltet_seit || '').slice(0, 10));
        return { stand: 'veraltet', standText: seit ? `überholt seit ${seit}` : 'überholt' };
    }
    if (b.befund === 'geaendert') return { stand: 'geaendert', standText: 'Zeitraum verschoben' };
    return { stand: 'aktuell', standText: 'aktuell' };
};

/**
 * Alle bestaetigten Bewertungen, schwerste zuerst — die Grundlage des PDF.
 *
 * ═══ JEDE FESTLEGUNG TRAEGT IHREN STAND ══════════════════════════════════
 * Bestaetigt heisst nicht aktuell. Eine Festlegung vom Juni steht auch dann
 * noch in dieser Liste, wenn der Plan die Gewerke inzwischen im Oktober oder
 * gar nicht mehr gleichzeitig fuehrt — geloescht wird sie nie, sie ist der
 * beweiskraeftige Teil. Ungekennzeichnet gelesen behauptet sie aber, der
 * Terminstand gelte weiter. Deshalb kommt `stand` mit; das PDF druckt ihn als
 * eigene Spalte, und keine Zeile faellt dabei weg.
 */
export const bestaetigteBewertungen = (zeilen: AbgleichZeile[]): BestaetigteFestlegung[] => {
    const raus: BestaetigteFestlegung[] = [];
    sortiereZeilen(zeilen || []).forEach(z => {
        z.bewertungen.forEach(b => {
            if (b.zeile.bestaetigt_am) raus.push({ zeile: z, bewertung: b, ...standAus(b) });
        });
    });
    return raus;
};

/** Bestaetigte Festlegungen, die sich NICHT mehr auf den heutigen Terminstand
 *  beziehen. Eigene Funktion, damit Bericht und Oberflaeche dieselbe Zahl
 *  nennen und nicht jede Stelle neu filtert. */
export const ueberholteFestlegungen = (zeilen: AbgleichZeile[]): BestaetigteFestlegung[] =>
    bestaetigteBewertungen(zeilen).filter(f => f.stand !== 'aktuell');

// ═══════════════════════════════════════════════════════════════════════════
// 6) MATRIX
// ═══════════════════════════════════════════════════════════════════════════

export interface MatrixZelle {
    /** `true` auf der Diagonale — ein Gewerk gefaehrdet sich nicht selbst. */
    diagonale: boolean;
    schluessel: string | null;
    dauerTage: number;
    ampel: Ampel;
    status: PaarStatus | null;
    bestaetigt: number;
    unbestaetigt: number;
}

export interface MatrixDaten {
    gewerke: GewerkAusPlan[];
    zellen: MatrixZelle[][];
}

/**
 * Die Kreuzmatrix. Nur Gewerke mit verwertbarem Zeitraum — die anderen stehen
 * in einer eigenen Liste darunter und nicht als leere Zeile in der Matrix, wo
 * sie wie "geprueft, kein Befund" aussaehen.
 */
export const baueMatrix = (gewerke: GewerkAusPlan[], zeilen: AbgleichZeile[]): MatrixDaten => {
    const liste = sortiereNachZeit(rechenbareGewerke(gewerke || []));
    const nachKey = new Map<string, AbgleichZeile>();
    (zeilen || []).forEach(z => nachKey.set(z.schluessel, z));

    const zellen: MatrixZelle[][] = liste.map(a => liste.map(b => {
        if (a.norm === b.norm) {
            return {
                diagonale: true, schluessel: null, dauerTage: 0,
                ampel: 'grau' as Ampel, status: null, bestaetigt: 0, unbestaetigt: 0,
            };
        }
        const key = paarKey(a.norm, b.norm);
        const z = nachKey.get(key);
        return {
            diagonale: false,
            schluessel: z ? key : null,
            dauerTage: z ? z.dauerTage : 0,
            ampel: z ? z.ampel : ampel(0),
            status: z ? z.status : null,
            bestaetigt: z ? z.bestaetigt : 0,
            unbestaetigt: z ? z.unbestaetigt : 0,
        };
    }));

    return { gewerke: liste, zellen };
};

// ═══════════════════════════════════════════════════════════════════════════
// 7) ZEITACHSE
// ═══════════════════════════════════════════════════════════════════════════

const MONATSNAMEN = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const schaltjahr = (jahr: number): boolean =>
    (jahr % 4 === 0 && jahr % 100 !== 0) || jahr % 400 === 0;

/** Tage im Monat (1..12) — ohne `Date`, damit die Zeitzone nichts zu sagen hat. */
export const tageImMonat = (jahr: number, monat: number): number => {
    if (monat === 2) return schaltjahr(jahr) ? 29 : 28;
    return [4, 6, 9, 11].indexOf(monat) >= 0 ? 30 : 31;
};

const iso = (jahr: number, monat: number, tag: number): string =>
    `${String(jahr).padStart(4, '0')}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;

export interface MonatsSpalte {
    jahr: number;
    monat: number;
    label: string;
    /** Lage im Gesamtzeitraum, 0..1. */
    anteilVon: number;
    anteilBis: number;
}

export interface Zone {
    von: string;
    bis: string;
    anteilVon: number;
    anteilBis: number;
}

export interface Balken {
    norm: string;
    label: string;
    von: string;
    bis: string;
    anteilVon: number;
    anteilBis: number;
    /** Die Abschnitte, in denen dieses Gewerk mit mindestens einem anderen
     *  zusammentrifft — zusammengefasst, damit sich die Bloecke nicht stapeln. */
    zonen: Zone[];
}

export interface ZeitachsenDaten {
    start: string;
    ende: string;
    spanneTage: number;
    monate: MonatsSpalte[];
    balken: Balken[];
}

/** Hoechstzahl der Monatsspalten, wie im Plotter-Generator (dort ebenfalls 60):
 *  eine Zeitachse ueber mehr als fuenf Jahre ist kein Terminplan mehr. */
export const MAX_MONATE = 60;

/**
 * Das Monatsraster der Zeitachse.
 *
 * Vorbild ist der Plotterplan (`generatorSigePlan.ts:146-160`): Kalendermonate
 * als Kopfzeile, Balken linear nach echtem Datum, Hoechstzahl 60.
 *
 * ABWEICHUNG: dort bekommen alle Monate dieselbe Breite (`monatW =
 * TIME_W / monate.length`), waehrend die Balken linear nach Tagen liegen — bei
 * ungleich langen Monaten laufen Kopfzeile und Balken deshalb um bis zu
 * anderthalb Tage auseinander. Hier ist die Spaltenbreite proportional zur
 * echten Monatslaenge; damit sitzt der Balkenanfang unter dem richtigen Monat.
 * Und wie dort wird ohne `Date` gerechnet (siehe `ueberschneidung.ts`).
 */
export const baueZeitachse = (
    gewerke: GewerkAusPlan[],
    paare: UeberschneidungsPaar[],
    projektVon?: string | null,
    projektBis?: string | null,
): ZeitachsenDaten | null => {
    const liste = sortiereNachZeit(rechenbareGewerke(gewerke || []));
    if (liste.length === 0) return null;

    let start = liste[0].von as string;
    let ende = liste[0].bis as string;
    liste.forEach(g => {
        if ((g.von as string) < start) start = g.von as string;
        if ((g.bis as string) > ende) ende = g.bis as string;
    });
    const pv = alsTag(projektVon);
    const pb = alsTag(projektBis);
    if (pv && pv < start) start = pv;
    if (pb && pb > ende) ende = pb;

    // Auf ganze Monate aufziehen — sonst begaenne die Kopfzeile mitten im Monat.
    const startJahr = Number(start.slice(0, 4));
    const startMonat = Number(start.slice(5, 7));
    const rasterStart = iso(startJahr, startMonat, 1);
    const endeJahr = Number(ende.slice(0, 4));
    const endeMonat = Number(ende.slice(5, 7));
    const rasterEnde = iso(endeJahr, endeMonat, tageImMonat(endeJahr, endeMonat));

    const spanneTage = tageEinschliesslich(rasterStart, rasterEnde);
    const klemme = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
    const anteil = (tag: string): number => klemme((tageEinschliesslich(rasterStart, tag) - 1) / spanneTage);
    /** Das Ende eines Balkens schliesst seinen letzten Tag MIT ein — sonst
     *  endete ein eintaegiger Einsatz auf einem Strich der Breite null. */
    const anteilEnde = (tag: string): number => klemme(tageEinschliesslich(rasterStart, tag) / spanneTage);

    const monate: MonatsSpalte[] = [];
    let j = startJahr;
    let m = startMonat;
    while (monate.length < MAX_MONATE) {
        monate.push({
            jahr: j,
            monat: m,
            label: `${MONATSNAMEN[m - 1]} ${String(j).slice(2)}`,
            anteilVon: anteil(iso(j, m, 1)),
            anteilBis: anteilEnde(iso(j, m, tageImMonat(j, m))),
        });
        if (j === endeJahr && m === endeMonat) break;
        m += 1;
        if (m > 12) { m = 1; j += 1; }
    }

    // Ueberschneidungszonen je Gewerk sammeln und zusammenfassen.
    const zonenRoh = new Map<string, { von: string; bis: string }[]>();
    (paare || []).forEach(p => {
        [p.aNorm, p.bNorm].forEach(norm => {
            const vorhanden = zonenRoh.get(norm);
            if (vorhanden) vorhanden.push({ von: p.von, bis: p.bis });
            else zonenRoh.set(norm, [{ von: p.von, bis: p.bis }]);
        });
    });

    const zusammenfassen = (roh: { von: string; bis: string }[]): Zone[] => {
        const sortiert = [...roh].sort((x, y) => vgl(x.von, y.von) || vgl(x.bis, y.bis));
        const raus: Zone[] = [];
        sortiert.forEach(z => {
            const letzte = raus[raus.length - 1];
            // `<=` fasst auch aneinandergrenzende Fenster zusammen; zwei Bloecke
            // Kante an Kante saehen ohnehin wie einer aus.
            if (letzte && z.von <= letzte.bis) {
                if (z.bis > letzte.bis) {
                    letzte.bis = z.bis;
                    letzte.anteilBis = anteilEnde(z.bis);
                }
                return;
            }
            raus.push({ von: z.von, bis: z.bis, anteilVon: anteil(z.von), anteilBis: anteilEnde(z.bis) });
        });
        return raus;
    };

    const balken: Balken[] = liste.map(g => ({
        norm: g.norm,
        label: g.label,
        von: g.von as string,
        bis: g.bis as string,
        anteilVon: anteil(g.von as string),
        anteilBis: anteilEnde(g.bis as string),
        zonen: zusammenfassen(zonenRoh.get(g.norm) || []),
    }));

    return { start: rasterStart, ende: rasterEnde, spanneTage, monate, balken };
};

// ═══════════════════════════════════════════════════════════════════════════
// 8) ALLGEMEINE HINWEISE
// ═══════════════════════════════════════════════════════════════════════════

export interface Gleichzeitigkeit {
    anzahl: number;
    tag: string | null;
    gewerke: string[];
}

/**
 * Der Tag mit den meisten gleichzeitig taetigen Gewerken.
 *
 * Geprueft werden nur die BEGINN-Tage: das Maximum kann sich nur dort aendern,
 * wo ein Gewerk hinzukommt. Bei Gleichstand gewinnt der fruehere Tag — die
 * Besprechung soll vor der Haeufung liegen, nicht danach.
 */
export const maximaleGleichzeitigkeit = (gewerke: GewerkAusPlan[]): Gleichzeitigkeit => {
    const liste = rechenbareGewerke(gewerke || []);
    const tage = [...new Set(liste.map(g => g.von as string))].sort(vgl);
    let beste: Gleichzeitigkeit = { anzahl: 0, tag: null, gewerke: [] };
    tage.forEach(tag => {
        const aktiv = liste.filter(g => (g.von as string) <= tag && tag <= (g.bis as string));
        if (aktiv.length > beste.anzahl) {
            beste = { anzahl: aktiv.length, tag, gewerke: aktiv.map(g => g.label) };
        }
    });
    return beste;
};

export interface AllgemeinerHinweis {
    regel: AllgemeineRegel;
    begruendung: string;
}

/**
 * Die Regeln, die allein aus der Lage folgen — ohne Ansehen der Gewerke.
 *
 * AR-05 (`anhang_zwei_parallel`) wird hier NICHT ausgewertet: welche Arbeit
 * unter Anhang II BaustellV faellt, steht in der Vorankuendigung
 * (`report.vorankuendigung.selectedDangerousWorks`) und nicht in den Daten, die
 * dieses Modul liest. Sie aus `Arbeitsschritt.kritisch` abzuleiten waere
 * geraten — und ein geratenes Anhang-II-Merkmal im SiGe-Plan ist schlimmer als
 * keines.
 */
export const allgemeineHinweise = (
    gewerke: GewerkAusPlan[],
    paare: UeberschneidungsPaar[],
): AllgemeinerHinweis[] => {
    const raus: AllgemeinerHinweis[] = [];
    const regel = (bedingung: AllgemeineRegel['bedingung']) =>
        ALLGEMEINE_REGELN.find(r => r.bedingung === bedingung);

    const spitze = maximaleGleichzeitigkeit(gewerke);
    const ar01 = regel('mehr_als_schwelle');
    if (ar01 && spitze.anzahl > SCHWELLE_KOORDINIERUNGSBESPRECHUNG) {
        raus.push({
            regel: ar01,
            begruendung: `Am ${spitze.tag} sind ${spitze.anzahl} Gewerke gleichzeitig auf der Baustelle: `
                + `${spitze.gewerke.join(', ')}.`,
        });
    }

    const ar02 = regel('mehrere_arbeitgeber');
    if (ar02 && (paare || []).length > 0) {
        raus.push({ regel: ar02, begruendung: `${paare.length} Gewerkepaar(e) arbeiten zeitgleich.` });
    }

    const offen = nichtZugeordnete(gewerke || []);
    const ar03 = regel('gewerk_unbekannt');
    if (ar03 && offen.length > 0) {
        raus.push({ regel: ar03, begruendung: `Nicht zugeordnet: ${offen.map(g => g.label).join(', ')}.` });
    }

    // ═══ AR-04 SPRICHT VOM ARBEITSSCHRITT, NICHT VOM GEWERK ══════════════
    // Bis hierher feuerte die Regel nur ueber die Aggregatliste, also nur fuer
    // Gewerke, deren Huelle GANZ fehlt. Ein Gewerk mit einem Schritt „ab
    // 20.09., Ende offen" hat eine gueltige Huelle aus seinen uebrigen
    // Schritten und kam deshalb in keiner Liste vor — der undatierte Schritt
    // verschwand spurlos. Beide Faelle gehoeren unter AR-04, und der zweite ist
    // der gefaehrlichere: er sieht aus wie ein geprueftes Gewerk.
    const ausschuss = ausschussListe(gewerke || []);
    const luecke = gewerkeMitLuecke(gewerke || []);
    const ar04 = regel('zeitraum_fehlt');
    if (ar04 && (ausschuss.length > 0 || luecke.length > 0)) {
        const teile: string[] = [];
        if (ausschuss.length > 0) {
            teile.push(`Ohne verwertbaren Zeitraum: ${ausschuss.map(a => a.gewerk.label).join(', ')}.`);
        }
        if (luecke.length > 0) {
            teile.push(
                'Mit Arbeitsschritten ohne Datum: '
                + luecke.map(g => `${g.label} (${g.schritteOhneDatum} Schritt(e))`).join(', ')
                + '. Der gerechnete Zeitraum dieser Gewerke schließt diese Schritte NICHT ein.',
            );
        }
        raus.push({ regel: ar04, begruendung: teile.join(' ') });
    }

    return raus;
};

/** Die Katalogfassung, unter der dieses Modul gerade arbeitet. Sie steht am
 *  Datensatz und im PDF — waechst der Katalog, aendert sich nicht rueckwirkend,
 *  was in einem abgeschlossenen Projekt galt. */
export const aktuelleKatalogVersion = (): string => String(KATALOG_VERSION);
